import { getAPIUrl } from "../config";

const API_URL = getAPIUrl();

const authedFetch = async (getAccessTokenSilently, path, options = {}) => {
  const token = await getAccessTokenSilently();
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const message = payload.error || `Request failed (${response.status})`;
    const code = payload.code ? ` [${payload.code}]` : "";
    const error = new Error(`${message}${code}`);
    error.code = payload.code || null;
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  if (response.status === 204) return null;
  return response.json();
};

export const fetchMe = async (getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, "/api/v1/me", { method: "GET" });

export const updateVenueOrderStatus = async (venueId, orderId, status, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/orders/${orderId}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });

export const getVenueProductionQueue = async (venueId, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/production`, {
    method: "GET",
  });

export const updateOrderItemStatus = async (venueId, orderItemId, status, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/order-items/${orderItemId}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });

export const updateVenueItemAvailability = async (venueId, itemId, isAvailable, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/items/${itemId}/availability`, {
    method: "PATCH",
    body: JSON.stringify({ isAvailable }),
  });

export const updateVenueRequestStatus = async (venueId, requestId, status, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/requests/${requestId}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });

export const patchVenueOrderItems = async (venueId, orderId, changes, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/orders/${orderId}/items`, {
    method: "PATCH",
    body: JSON.stringify({ changes }),
  });

export const reorderVenueTables = async (venueId, tables, getAccessTokenSilently) => {
  const payload = {
    tables: tables.map((table, index) => ({
      id: table.id,
      sortOrder: index,
    })),
  };

  return authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/tables/reorder`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
};

export const settleVenueTable = async (venueId, tableId, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/tables/${tableId}/settle`, {
    method: "POST",
  });

export const transferVenueTable = async (
  venueId,
  sourceTableId,
  destinationTableId,
  getAccessTokenSilently
) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/tables/${sourceTableId}/transfer`, {
    method: "POST",
    body: JSON.stringify({ destinationTableId }),
  });

export const createVenueOrder = async (venueId, order, getAccessTokenSilently) => {
  return authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/orders`, {
    method: "POST",
    body: JSON.stringify({
      tableId: order.tableId,
      status: order.status || "pending",
      notes: order.notes || undefined,
      items: (order.items || [])
        .filter((item) => item && item.id && item.quantity > 0)
        .map((item) => ({
          item_id: item.id,
          quantity: item.quantity,
          notes: item.notes || undefined,
          selected_modifier_options: (item.selectedModifierOptions || []).map((selection) => ({
            group_id: selection.group_id || selection.groupId,
            option_id: selection.option_id || selection.optionId,
          })),
        })),
    }),
  });
};

export const openVenueTableCheck = async (venueId, tableId, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/tables/${tableId}/check`, {
    method: "POST",
  });

export const getVenueCheck = async (venueId, checkId, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/checks/${checkId}`, {
    method: "GET",
  });

export const addVenueCheckItems = async (venueId, checkId, payload, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/checks/${checkId}/items`, {
    method: "POST",
    body: JSON.stringify({
      orderNotes: payload.orderNotes || undefined,
      items: (payload.items || [])
        .filter((item) => item && item.id && item.quantity > 0)
        .map((item) => ({
          item_id: item.id,
          quantity: item.quantity,
          notes: item.notes || undefined,
          selected_modifier_options: (item.selectedModifierOptions || []).map((selection) => ({
            group_id: selection.group_id || selection.groupId,
            option_id: selection.option_id || selection.optionId,
          })),
        })),
    }),
  });

export const addVenueCheckDiscount = async (venueId, checkId, payload, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/checks/${checkId}/discounts`, {
    method: "POST",
    body: JSON.stringify({
      type: payload.type,
      amount: Number(payload.amount),
      reason: payload.reason || undefined,
    }),
  });

export const addVenueCheckPayment = async (venueId, checkId, payload, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/checks/${checkId}/payments`, {
    method: "POST",
    body: JSON.stringify({
      method: payload.method,
      amount: Number(payload.amount),
      note: payload.note || undefined,
    }),
  });

export const closeVenueCheck = async (venueId, checkId, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/checks/${checkId}/close`, {
    method: "POST",
  });

export const voidVenueCheckItem = async (venueId, checkId, orderItemId, payload, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/checks/${checkId}/items/${orderItemId}/void`, {
    method: "POST",
    body: JSON.stringify({
      quantity: payload?.quantity,
      reason: payload?.reason,
    }),
  });

export const openVenueShift = async (venueId, payload, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/shifts`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const getCurrentVenueShift = async (venueId, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/shifts/current`, {
    method: "GET",
  });

export const closeVenueShift = async (venueId, payload, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/shifts/current/close`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const listVenueShifts = async (venueId, getAccessTokenSilently) =>
  authedFetch(getAccessTokenSilently, `/api/v1/venues/${venueId}/shifts`, {
    method: "GET",
  });
